import React from "react";
import { Instagram, ArrowUpRight } from "lucide-react";
import { CONTACT, INSTAGRAM_REELS } from "../lib/constants";

function ReelTile({ reel, idx }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.35 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return (
    <a
      data-testid={`instagram-reel-${idx}`}
      href={CONTACT.instagram}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block aspect-[9/14] overflow-hidden rounded-sm gold-border bg-white tilt-card"
    >
      <video
        ref={ref}
        src={reel.src}
        muted
        loop
        playsInline
        autoPlay
        preload="auto"
        poster=""
        className="w-full h-full object-cover bg-[#EFE6D9]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#4A3F35]/55 via-transparent to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
      <div className="absolute top-3 right-3 w-8 h-8 grid place-items-center bg-white/90 backdrop-blur rounded-full">
        <Instagram size={13} className="text-[#B5896E]" />
      </div>
      <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
        <div className="text-xs text-white tracking-[0.18em] uppercase font-medium drop-shadow">{reel.caption}</div>
        <ArrowUpRight size={16} className="text-white opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
      </div>
    </a>
  );
}

export default function InstagramReels() {
  return (
    <section data-testid="home-instagram-reels" className="py-24 lg:py-32 bg-[#F4E8DC]/40 border-y border-[#B5896E]/15">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between gap-8 mb-14 flex-wrap">
          <div className="max-w-2xl">
            <div className="caption text-[#B5896E] mb-5 flex items-center gap-3">
              <Instagram size={14} /> {CONTACT.instagramHandle}
            </div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] text-[#4A3F35]">
              Live from the <em className="italic text-[#B5896E]">atelier.</em>
            </h2>
            <p className="mt-5 text-base lg:text-lg text-[#4A3F35]/70 leading-relaxed">
              A glimpse into pieces leaving our bench — captured the day they're made. Follow along on Instagram for new arrivals, behind-the-scenes craft, and bridal edits.
            </p>
          </div>
          <a
            data-testid="instagram-cta"
            href={CONTACT.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 text-xs tracking-[0.22em] uppercase border border-[#B5896E]/50 text-[#4A3F35] hover:bg-[#B5896E] hover:text-white hover:border-[#B5896E] rounded-full transition-colors duration-400"
          >
            <Instagram size={14} /> Follow on Instagram
            <ArrowUpRight size={14} />
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
          {INSTAGRAM_REELS.map((r, i) => (
            <ReelTile key={r.src} reel={r} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
